import { computed, inject } from 'vue';
import { IKWorkloadsColumnsManagement } from '@/views/workloads/workloads.injectionKeys';
import { useOsService } from '@/services/OsService/OsService';
import { app } from '@/bootloader';
import { useWorkloadsTableConstants } from '@/views/workloads/workloads.const';
import { IKWorkloadsPersistentData, WorkloadsTableLinuxColumns, WorkloadsTableMacColumns, WorkloadsTableWindowsColumns } from '@/views/workloads/workloads-table.types';
import { WorkloadsPagePersistentDataType } from '@/views/workloads/workloads.helpers';

export const useWorkloadsColumnsManagementProvider = () => {
  const workloadsPagePersistentData = inject(IKWorkloadsPersistentData);

  const osService = useOsService();
  const workloadsTableConstants = useWorkloadsTableConstants();
  const defaultColumnsData = computed(() => {
    const viewableColumns = workloadsTableConstants.columns.value.filter(column => workloadsTableConstants.viewableColumns.value.has(column.name));

    return {
      columnsFilter: viewableColumns.reduce(
        (acc, cur) => ({
          ...acc,
          [cur.name]: workloadsTableConstants.defaultColumns.value.has(cur.name as any),
        }),
        {},
      ) as Record<WorkloadsTableWindowsColumns, boolean> | Record<WorkloadsTableMacColumns, boolean> | Record<WorkloadsTableLinuxColumns, boolean>,
      columnsOrder: viewableColumns,
    };
  });

  const containsPersistentDataTableKeys = (persistentData: object) =>
    Object.prototype.hasOwnProperty.call(persistentData, 'columnsOrder') && Object.prototype.hasOwnProperty.call(persistentData, 'columnsFilter');

  const linuxGetEditorData = computed(() => {
    const tableEditorData = workloadsPagePersistentData?.getTableEditorData.value;
    return containsPersistentDataTableKeys(tableEditorData) ? tableEditorData : defaultColumnsData.value;
  });

  const windowsGetEditorData = computed(() => {
    const tableEditorData = workloadsPagePersistentData?.getTableEditorData.value;
    return containsPersistentDataTableKeys(tableEditorData) ? tableEditorData : defaultColumnsData.value;
  });

  const tableEditor = {
    get: {
      windows: windowsGetEditorData,
      linux: linuxGetEditorData,
      mac: linuxGetEditorData,
    },
    set: {
      linux: (value: any) => {
        workloadsPagePersistentData?.setTableEditorData(value);
      },
      mac: (value: any) => {
        workloadsPagePersistentData?.setTableEditorData(value);
      },
      windows: (value: any) => {
        workloadsPagePersistentData?.setTableEditorData(value);
      },
    },
  };

  const tableColumns = {
    windows: computed(() => tableEditor.get.windows.value.columnsOrder.filter((column: any) => tableEditor.get.windows.value.columnsFilter[column.name])),
    linux: computed(() => tableEditor.get.linux.value.columnsOrder.filter((column: any) => tableEditor.get.linux.value.columnsFilter[column.name])),
    mac: computed(() => tableEditor.get.mac.value.columnsOrder.filter((column: any) => tableEditor.get.mac.value.columnsFilter[column.name])),
  };

  const resetColumnsToDefault = () => {
    if (osService?.isUnix.value) {
      tableEditor.set.linux(defaultColumnsData.value as WorkloadsPagePersistentDataType['tableEditor']['linux']);
    }
    if (osService?.isWindows.value) {
      tableEditor.set.windows(defaultColumnsData.value as WorkloadsPagePersistentDataType['tableEditor']['windows']);
    }
  };

  const payload = { tableColumns, tableEditor, resetColumnsToDefault };

  app.provide(IKWorkloadsColumnsManagement, payload);
  return payload;
};

export const useWorkloadsColumnsManagement = () => {
  const payload = inject(IKWorkloadsColumnsManagement);
  if (!payload) {
    throw new Error('useWorkloadsColumnsManagement must be used within a useWorkloadsColumnsManagementProvider');
  }
  return payload;
};
